/**
 * The Pipe17 purchase-order timeline, mapped onto our ASN lifecycle.
 *
 * Pipe17's own PO detail page renders a six-row Timeline card. It is NOT an
 * event log — it is six FIELDS off the purchase record laid out as a stepper,
 * in a fixed order that is deliberately not chronological (Expected Ship and
 * Expected Arrival sit between Ingested and ASN Created even when their dates
 * are earlier). We mirror those six rows, in that order, so an operator
 * comparing the two portals sees the same card in the same shape.
 *
 * WHAT MAKES A ROW GREEN IS OUR STATUS, NOT THE PRESENCE OF A DATE.
 *
 * The first cut of this file did the latter, and it decoupled the card from
 * the row it sits under: a PO reading "Picked Up" in the STATUS column showed
 * five green rows that said nothing about pickup, and one at Pipe17's
 * `partialFulfillment` was indistinguishable from a brand-new import. Every
 * row is now bound to one rung of the ASN lifecycle (control-tower's
 * ASN_STATUSES) and lights up when the ASN has reached that rung — so the
 * stepper and the STATUS column can never disagree.
 *
 * The bindings follow pipe17-provider's PURCHASE_STATUS_MAP, which already
 * decides which Pipe17 state means which of our statuses. Two are a stretch
 * and are worth knowing about:
 *
 *   Ingested -> Supplier Notified. "Ingested" is Pipe17 accepting the PO into
 *     its own system, not a supplier event. It is bound here because that is
 *     the rung at this position, and because Pipe17 cannot transmit to the
 *     supplier (`sentToFulfillment` -> Supplier Notified) before it has
 *     ingested.
 *   ASN Created -> Reached Warehouse. Pipe17 means its own inbound-receipt
 *     record. We DO now import those (asn.arrivals[].receipts[], via
 *     pipe17-provider's arrivals/receipts walk), but this row stays bound to
 *     the ASN lifecycle like every other one — binding it to "a receipt
 *     exists" would light it on a PARTIALLY received PO, which is the exact
 *     class of confusion the status binding exists to prevent. The receipts
 *     are used for the Received row's DATE, where they are strictly better
 *     than what we had.
 *
 * Dates come from the Pipe17 fields alone, never from the trail. A trail row
 * records when a status was reached, which is a different fact from the date
 * the row names — stamping the "Picked Up" trail time under "Expected
 * Arrival" would turn an expectation into an event.
 */

/**
 * ASN lifecycle ranks, verbatim from control-tower's `STATIC_DATA.ASN_STATUSES`
 * (utils/constants.js). The whole ladder is here, not just the six bound
 * rungs, so an ASN sitting on an unbound status (Partially Fulfilled,
 * Pickup Scheduled, Partially Received) still ranks correctly against them.
 */
const ASN_RANK = {
  Pending: 0,
  Created: 10,
  "On Hold": 15,
  "Supplier Notified": 20,
  "Supplier Confirmed": 30,
  "Partially Fulfilled": 35,
  "Inventory Ready": 40,
  "Pickup Scheduled": 50,
  "Picked Up": 60,
  "Reached Warehouse": 70,
  "Partially Received": 75,
  "Stock-in Complete": 80,
};

/**
 * Terminal statuses that rank ABOVE Stock-in Complete in ASN_STATUSES (90 and
 * 100) so that nothing can walk an ASN back out of them. Ranking them here
 * would light every row green on a cancelled PO, which reads as "fully
 * received". They are off the forward path, so no row is claimed for them —
 * the STATUS column is what says the PO was cancelled or returned.
 */
const OFF_PATH_STATUSES = ["Cancelled", "Returned to Supplier"];

/** Statuses that mean the goods physically arrived, in either portal's words. */
const RECEIVED_PROVIDER_STATUSES = ["received", "closed"];

/**
 * Whether this ASN came from Pipe17 rather than the portal.
 *
 * `others.oms_provider` is stamped by pipe17-provider's normalize/purchase.js
 * on every PO it maps, and is the only marker that survives the control-tower
 * upsert (the ASN itself has no source field).
 */
export const isPipe17Po = (asn) => asn?.others?.oms_provider === "Pipe17";

/**
 * When goods were last booked in against this PO.
 *
 * Prefers the provider's own goods-in receipts (`arrivals[].receipts[]`), which
 * carry the only true received-on timestamp in the Pipe17 chain — neither the
 * arrival nor the purchase record has one. Falls back to the portal's
 * `fulfiled_items[].received_date`, which is what a portal-driven stock-in
 * writes and is all a non-provider PO has.
 *
 * Deliberately NOT `others.actual_arrival_date`: the provider derives that from
 * the most recent ARRIVAL, so it moves when goods reach the door rather than
 * when they are counted in, and this row is about receipt.
 */
const latestReceivedDate = (asn) => {
  const times = [
    ...(asn?.arrivals || []).flatMap((a) =>
      (a?.receipts || []).map((r) => r?.actual_arrival_date),
    ),
    ...(asn?.fulfiled_items || []).map((f) => f?.received_date),
  ]
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !isNaN(t));
  if (!times.length) return null;
  return new Date(Math.max(...times)).toISOString();
};

/**
 * ASN document -> the six Pipe17 timeline rows.
 *
 * Each row is `{ label, status, date, reached }`. `status` is the ASN status
 * the row stands for — the caller surfaces it as a tooltip so the binding is
 * inspectable rather than folklore. `date` may be null on a reached row: the
 * status can be reached while Pipe17 never sent the corresponding date, and
 * an invented stamp would read as fact.
 */
export const buildPipe17Timeline = (asn) => {
  const others = asn?.others || {};

  const currentRank = OFF_PATH_STATUSES.includes(asn?.status)
    ? -1
    : (ASN_RANK[asn?.status] ?? -1);

  const rows = [
    // The date the PO was raised at the supplier. `po_date` is the stored
    // field; the raw name is the fallback for POs imported before po_date
    // existed, matching formatPoDate's chain.
    {
      label: "Ordered",
      status: "Created",
      date: asn?.po_date || others.purchase_order_date,
    },
    // When the PO first appeared in PIPE17 — their ingest, not ours.
    {
      label: "Ingested",
      status: "Supplier Notified",
      date: others.pipe17_ingested_at,
    },
    // Pipe17 `shipByDate`: when the supplier must dispatch.
    {
      label: "Expected Ship",
      status: "Supplier Confirmed",
      date: asn?.expected_dispatch_date,
    },
    {
      label: "Expected Arrival",
      status: "Picked Up",
      date: others.expected_arrival_date,
    },
    // Our ASN document's own creation — see the header note.
    {
      label: "ASN Created",
      status: "Reached Warehouse",
      date: asn?.createdAt,
    },
    {
      label: "Received",
      status: "Stock-in Complete",
      date: latestReceivedDate(asn),
    },
  ];

  return rows.map((r) => ({
    label: r.label,
    status: r.status,
    date: r.date || null,
    reached:
      currentRank >= ASN_RANK[r.status] ||
      // Receipt is the one milestone the provider can assert on its own: a
      // Pipe17 `received`/`closed` PO is stocked in upstream even when our own
      // ASN is not, because control-tower REFUSES to apply a stock-moving
      // status through the oms-sync `_patch` (it bypasses the hooks that do
      // the real stock-in — see oms-sync-subscriber STOCK_MOVING_STATUSES).
      // Without this the row would stay grey on exactly the POs Pipe17 shows
      // as received.
      (r.label === "Received" &&
        RECEIVED_PROVIDER_STATUSES.includes(
          String(others.pipe17_status || "").toLowerCase(),
        )),
  }));
};
